import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App as AntApp, ConfigProvider } from "antd";
import zhCN from "antd/locale/zh_CN";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { antdTheme } from "@nimbus-erp/ui";
import "@nimbus-erp/ui/styles.css";
import "./styles/erp-shell.css";
import "./styles/erp-login.css";
import "./styles/erp-workbench.css";
import "./styles/erp-trade.css";
import "./styles/erp-refresh.css";
import "./styles/erp-modern.css";
import "./styles/erp-finance.css";
import "./styles/erp-density.css";
import "./styles/erp-brand-v2.css";
import "./styles/erp-brand-v3.css";
import "./styles/erp-brand-v4.css";
import "./styles/erp-operational-workspace.css";
import "./styles/erp-reference-blue.css";
import "./styles/erp-interaction-polish.css";
import "./styles/erp-dialogs.css";
import "./styles/erp-pagination.css";
import { router } from "./router";
import { ErpLoadingMark } from "./components/ErpLoading";
import { ErpDialogBehavior } from "./components/ErpDialogBehavior";

const erpTheme = {
  ...antdTheme,
  token: {
    ...antdTheme.token,
    colorPrimary: "#2f78e8",
    colorLink: "#2469cf",
    colorInfo: "#438cf0",
    colorSuccess: "#20a878",
    colorWarning: "#d99224",
    colorError: "#d25561",
    colorText: "#18385f",
    colorTextSecondary: "#6c84a1",
    colorBgLayout: "#f4f8fd",
    colorBgContainer: "#ffffff",
    controlHeight: 34,
    controlHeightSM: 28,
    controlHeightLG: 40,
    fontSize: 13,
    colorBorder: "#ccdaea",
    colorBorderSecondary: "#dbe6f2",
    controlOutline: "rgba(47, 120, 232, 0.14)",
    borderRadius: 6,
    borderRadiusLG: 11,
    motionDurationFast: "0.14s",
    motionDurationMid: "0.2s",
    motionDurationSlow: "0.28s",
    boxShadowSecondary: "0 22px 64px rgba(35, 76, 126, 0.17)",
  },
  components: {
    ...antdTheme.components,
    Button: {
      ...antdTheme.components?.Button,
      defaultBorderColor: "#ccdaea",
      defaultColor: "#365779",
      primaryShadow: "0 6px 14px rgba(47, 120, 232, 0.18)",
    },
    Input: {
      ...antdTheme.components?.Input,
      activeBorderColor: "#6aa3eb",
      hoverBorderColor: "#8db7e8",
      activeShadow: "0 0 0 3px rgba(47, 120, 232, 0.1)",
    },
    Select: {
      optionActiveBg: "#eff6ff",
      optionSelectedBg: "#e4f0ff",
      optionSelectedColor: "#205fae",
    },
    Table: {
      ...antdTheme.components?.Table,
      headerBg: "#edf4fc",
      headerColor: "#4d6c91",
      rowHoverBg: "#eef6ff",
      rowSelectedBg: "#e5f1ff",
      rowSelectedHoverBg: "#dcecff",
    },
  },
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ConfigProvider
      locale={zhCN}
      theme={erpTheme}
      button={{ autoInsertSpace: false }}
      spin={{ indicator: <ErpLoadingMark /> }}
    >
      <AntApp>
        <ErpDialogBehavior />
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  </StrictMode>,
);
